import React, { useMemo } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CandidateMatchRow from './CandidateMatchRow'
import { MATCH_TIERS } from './candidateMatchUtils'

const TOP_CANDIDATE_DISPLAY_COUNT = 5

const QUALITY_CHIP_CONFIGS = [
  { key: 'excellent', label: MATCH_TIERS.EXCELLENT, getStyle: theme => ({ backgroundColor: theme.palette.success.main, color: 'white' }) },
  { key: 'strong', label: MATCH_TIERS.STRONG, getStyle: theme => ({ backgroundColor: theme.palette.info.main, color: 'white' }) },
  { key: 'potential', label: MATCH_TIERS.POTENTIAL, getStyle: theme => ({ backgroundColor: theme.palette.grey[500], color: 'white' }) },
]

const InlineCandidateMatchContent = ({
  job,
  matchedCandidates,
  isLoadingCandidates,
  onViewFullList,
}) => {
  const theme = useTheme()
  const topCandidates = matchedCandidates.slice(0, TOP_CANDIDATE_DISPLAY_COUNT)
  const counts = useMemo(() => {
    const result = { total: 0, excellent: 0, strong: 0, potential: 0 }
    matchedCandidates.forEach(candidate => {
      result.total++
      const tier = candidate.matchTier?.toLowerCase()
      if (tier in result) result[tier]++
    })
    return result
  }, [matchedCandidates])

  if (isLoadingCandidates) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (!job.candidateTypeId) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant='body2' color='text.secondary'>
          This job has no profession assigned. Add a candidate type to see matching candidates.
        </Typography>
      </Box>
    )
  }

  if (matchedCandidates.length === 0) {
    return (
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <Typography variant='body2' color='text.secondary'>
          No matching candidates found for this job.
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, pb: 1.5, flexWrap: 'wrap' }}>
        {QUALITY_CHIP_CONFIGS.filter(({ key }) => counts[key] > 0).map(({ key, label, getStyle }) => (
          <Chip
            key={key}
            label={`${counts[key]} ${label}`}
            size='small'
            sx={{
              ...getStyle(theme),
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          />
        ))}
      </Box>

      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Match</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Match Reasons</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Experience</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Specialty</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {topCandidates.map(candidate => (
            <CandidateMatchRow
              key={candidate.id}
              candidate={candidate}
              job={job}
            />
          ))}
        </TableBody>
      </Table>

      {counts.total > TOP_CANDIDATE_DISPLAY_COUNT && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: 1.5,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
            Showing top {topCandidates.length} of {counts.total}
          </Typography>
          <Button
            variant='outlined'
            size='small'
            endIcon={<ArrowForwardIcon />}
            onClick={() => onViewFullList(job.id)}
          >
            View Full List
          </Button>
        </Box>
      )}
    </>
  )
}

export default InlineCandidateMatchContent
